/**
 * SidebarNotifications
 *
 * Compact notifications widget for the navigation sidebar.
 * Shows recent unread notifications with quick actions.
 * Links to the full notifications page.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Bell,
    ChevronDown,
    ChevronUp,
    Layers,
    AlertCircle,
    Image,
    Video,
    Check,
    Clock,
    Calendar,
    FolderOpen,
    ExternalLink
} from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';

const SidebarNotifications = () => {
    const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
    const [expanded, setExpanded] = useState(false);
    const navigate = useNavigate();

    // Show the most recent 5 notifications (unread first)
    const recentNotifications = [...notifications]
        .sort((a, b) => {
            if (a.read !== b.read) return a.read ? 1 : -1;
            return 0; // preserve existing order (already sorted by createdAt desc)
        })
        .slice(0, 5);

    if (notifications.length === 0) {
        return null;
    }

    const formatTimeAgo = (date) => {
        if (!date) return '';
        const now = new Date();
        const then = new Date(date);
        const diffMs = now - then;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'now';
        if (diffMins < 60) return `${diffMins}m`;
        if (diffHours < 24) return `${diffHours}h`;
        return `${diffDays}d`;
    };

    const getIcon = (notification) => {
        switch (notification.type) {
            case 'generation_complete':
                return notification.data?.type === 'video'
                    ? <Video size={14} style={{ color: 'var(--success-500)' }} />
                    : <Image size={14} style={{ color: 'var(--success-500)' }} />;
            case 'generation_failed':
            case 'bulk_generation_failed':
                return <AlertCircle size={14} style={{ color: 'var(--error-500)' }} />;
            case 'bulk_generation_complete':
                return <Layers size={14} style={{ color: 'var(--success-500)' }} />;
            default:
                return <Bell size={14} style={{ color: 'var(--color-primary)' }} />;
        }
    };

    const handleNotificationClick = async (notification) => {
        if (!notification.read) {
            await markAsRead(notification.id);
        }

        if (notification.type === 'bulk_generation_complete') {
            navigate(`/library?planId=${notification.data?.planId}`);
        } else if (notification.chatId) {
            navigate(`/conversational?chatId=${notification.chatId}`);
        } else if (notification.data?.url) {
            window.open(notification.data.url, '_blank');
        } else {
            navigate('/notifications');
        }
        setExpanded(false);
    };

    return (
        <div style={{ position: 'relative' }}>
            {/* Indicator Button */}
            <button
                onClick={() => setExpanded(!expanded)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2) var(--space-3)',
                    background: unreadCount > 0 ? 'var(--primary-100)' : 'var(--color-bg-secondary)',
                    border: `1px solid ${unreadCount > 0 ? 'var(--primary-300)' : 'var(--color-border)'}`,
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-sm)',
                    color: unreadCount > 0 ? 'var(--primary-700)' : 'var(--color-text-secondary)',
                    fontWeight: 500,
                    width: '100%'
                }}
            >
                <Bell size={16} />
                <span style={{ flex: 1, textAlign: 'left' }}>
                    {unreadCount > 0 ? `${unreadCount} new` : 'Notifications'}
                </span>
                {unreadCount > 0 && (
                    <span style={{
                        background: 'var(--color-primary)',
                        color: 'white',
                        borderRadius: 'var(--radius-full)',
                        padding: '0 6px',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                        minWidth: '18px',
                        textAlign: 'center',
                        lineHeight: '18px'
                    }}>
                        {unreadCount}
                    </span>
                )}
                {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {/* Dropdown Panel */}
            {expanded && (
                <>
                    {/* Backdrop */}
                    <div
                        style={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: 999
                        }}
                        onClick={() => setExpanded(false)}
                    />

                    {/* Panel */}
                    <div style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: 0,
                        right: 0,
                        marginBottom: 'var(--space-2)',
                        width: '320px',
                        background: 'var(--color-bg-primary)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-lg)',
                        boxShadow: 'var(--shadow-lg)',
                        zIndex: 1000,
                        overflow: 'hidden'
                    }}>
                        {/* Header */}
                        <div style={{
                            padding: 'var(--space-3) var(--space-4)',
                            borderBottom: '1px solid var(--color-border)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)'
                            }}>
                                <Bell size={16} style={{ color: 'var(--color-primary)' }} />
                                <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>Notifications</span>
                            </div>
                            {unreadCount > 0 && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        markAllAsRead();
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: 'var(--space-1)',
                                        color: 'var(--color-primary)',
                                        fontSize: 'var(--font-size-xs)',
                                        fontWeight: 500
                                    }}
                                >
                                    Mark all read
                                </button>
                            )}
                        </div>

                        {/* Notifications List */}
                        <div style={{
                            maxHeight: '320px',
                            overflow: 'auto'
                        }}>
                            {recentNotifications.map(notification => (
                                <NotificationItem
                                    key={notification.id}
                                    notification={notification}
                                    getIcon={getIcon}
                                    formatTimeAgo={formatTimeAgo}
                                    onClick={() => handleNotificationClick(notification)}
                                    onMarkRead={() => markAsRead(notification.id)}
                                />
                            ))}
                        </div>

                        {/* Footer */}
                        <div style={{
                            padding: 'var(--space-3) var(--space-4)',
                            borderTop: '1px solid var(--color-border)',
                            background: 'var(--color-bg-secondary)'
                        }}>
                            <button
                                onClick={() => {
                                    navigate('/notifications');
                                    setExpanded(false);
                                }}
                                className="btn btn-ghost btn-sm"
                                style={{
                                    width: '100%',
                                    justifyContent: 'center',
                                    fontSize: 'var(--font-size-sm)'
                                }}
                            >
                                <ExternalLink size={14} />
                                View All Notifications
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

const NotificationItem = ({ notification, getIcon, formatTimeAgo, onClick, onMarkRead }) => {
    const isBulkComplete = notification.type === 'bulk_generation_complete';
    const isFailed = notification.type?.includes('failed');

    return (
        <div
            onClick={onClick}
            style={{
                padding: 'var(--space-3) var(--space-4)',
                borderBottom: '1px solid var(--color-border)',
                cursor: 'pointer',
                display: 'flex',
                gap: 'var(--space-3)',
                background: notification.read ? 'transparent' : 'var(--primary-50)',
                transition: 'background var(--transition-fast)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
            onMouseLeave={(e) => e.currentTarget.style.background = notification.read ? 'transparent' : 'var(--primary-50)'}
        >
            {/* Icon */}
            <div style={{
                flexShrink: 0,
                width: '28px',
                height: '28px',
                borderRadius: 'var(--radius-md)',
                background: isFailed ? 'var(--error-50)' : isBulkComplete ? 'var(--success-50)' : 'var(--primary-50)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                {getIcon(notification)}
            </div>

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 'var(--space-2)'
                }}>
                    <span style={{
                        fontWeight: notification.read ? 400 : 600,
                        fontSize: 'var(--font-size-sm)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>
                        {notification.title}
                    </span>
                    <span style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-tertiary)',
                        flexShrink: 0
                    }}>
                        {formatTimeAgo(notification.createdAt)}
                    </span>
                </div>
                <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginTop: '2px'
                }}>
                    {notification.message}
                </div>

                {/* Quick actions for bulk generation */}
                {isBulkComplete && notification.data?.successCount > 0 && (
                    <div style={{
                        marginTop: 'var(--space-2)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--success-600)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-1)'
                    }}>
                        <FolderOpen size={12} />
                        {notification.data.successCount} creatives ready - View in Library
                    </div>
                )}

                {/* Unread dot */}
                {!notification.read && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onMarkRead();
                        }}
                        style={{
                            marginTop: 'var(--space-1)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-primary)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}
                    >
                        <Check size={10} />
                        Mark read
                    </button>
                )}
            </div>
        </div>
    );
};

export default SidebarNotifications;
